import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError, api, primeCsrf, setActiveOrganisation, unwrap } from '../api/client';
import type {
  AppSettings,
  CurrentUser,
  EnumChoice,
  EnumName,
  OrganisationAccess,
} from '../api/types';
import { djangoTagFor, normaliseLanguage, setLanguage } from '../i18n';

const ORGANISATION_STORAGE_KEY = 'fly.organisation';

type SessionValue = {
  user: CurrentUser | null;
  settings: AppSettings | null;
  organisations: OrganisationAccess[];
  organisation: OrganisationAccess | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /** True when the signed-in user has no organisation, which no screen can work without. */
  hasNoAccess: boolean;
  /** True when `/auth/me` failed with something other than "not signed in", usually a down API. */
  backendUnavailable: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  selectOrganisation: (slug: string) => Promise<void>;
  can: (...permissions: string[]) => boolean;
  canAny: (...permissions: string[]) => boolean;
  /** Resolves an enum value to its server-side label, for values the UI has no catalog for. */
  enumLabel: (name: EnumName, value: string | null | undefined) => string;
  enumChoices: (name: EnumName) => EnumChoice[];
  refresh: () => Promise<void>;
};

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [organisationSlug, setOrganisationSlug] = useState<string | null>(() =>
    typeof localStorage === 'undefined' ? null : localStorage.getItem(ORGANISATION_STORAGE_KEY),
  );

  // The header has to be in place before any query runs, so it is set during render rather
  // than in an effect.
  setActiveOrganisation(organisationSlug);

  const userQuery = useQuery({
    queryKey: ['session', 'me'],
    queryFn: async () => {
      await primeCsrf();
      try {
        return unwrap(await api.GET('/api/v1/auth/me/'));
      } catch (error) {
        if (error instanceof ApiError && error.isUnauthenticated) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const user = userQuery.data ?? null;
  const organisations = useMemo(() => user?.organisations ?? [], [user]);

  const settingsQuery = useQuery({
    queryKey: ['session', 'settings', djangoTagFor(normaliseLanguage(i18n.language))],
    queryFn: async () => unwrap(await api.GET('/api/v1/settings/')),
    enabled: Boolean(user),
    staleTime: Infinity,
  });

  // A remembered organisation the user has since lost access to must not strand them, so the
  // selection falls back to whatever the server considers active.
  const organisation = useMemo(() => {
    if (!organisations.length) return null;
    const remembered = organisations.find((entry) => entry.slug === organisationSlug);
    if (remembered) return remembered;
    const fromServer = organisations.find((entry) => entry.id === user?.active_organisation);
    return fromServer ?? organisations.find((entry) => entry.is_primary) ?? organisations[0];
  }, [organisations, organisationSlug, user?.active_organisation]);

  useEffect(() => {
    if (organisation && organisation.slug !== organisationSlug) {
      setOrganisationSlug(organisation.slug);
    }
  }, [organisation, organisationSlug]);

  useEffect(() => {
    if (typeof localStorage !== 'undefined' && organisationSlug) {
      localStorage.setItem(ORGANISATION_STORAGE_KEY, organisationSlug);
    }
  }, [organisationSlug]);

  // A user with an explicit language preference gets it applied on every load, including on
  // a machine they have never used.
  useEffect(() => {
    if (user?.preferred_language) {
      void setLanguage(normaliseLanguage(user.preferred_language));
    }
  }, [user?.preferred_language]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const payload = unwrap(
        await api.POST('/api/v1/auth/login/', { body: { email, password } }),
      );
      const first =
        payload.organisations.find((entry) => entry.id === payload.active_organisation) ??
        payload.organisations.find((entry) => entry.is_primary) ??
        payload.organisations[0];
      if (first) {
        setOrganisationSlug(first.slug);
        setActiveOrganisation(first.slug);
      }
      queryClient.setQueryData(['session', 'me'], payload);
      await queryClient.invalidateQueries();
    },
    [queryClient],
  );

  const signOut = useCallback(async () => {
    try {
      await api.POST('/api/v1/auth/logout/');
    } finally {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(ORGANISATION_STORAGE_KEY);
      }
      setOrganisationSlug(null);
      setActiveOrganisation(null);
      queryClient.setQueryData(['session', 'me'], null);
      queryClient.clear();
    }
  }, [queryClient]);

  const selectOrganisation = useCallback(
    async (slug: string) => {
      setOrganisationSlug(slug);
      setActiveOrganisation(slug);
      // Telling the server too keeps the Unfold admin session on the same club.
      await api.POST('/api/v1/auth/switch-organisation/', { body: { slug } });
      await queryClient.invalidateQueries();
    },
    [queryClient],
  );

  const permissions = useMemo(() => {
    if (user?.is_superuser) return null; // null means "everything"
    return new Set(organisation?.permissions ?? []);
  }, [organisation, user?.is_superuser]);

  const can = useCallback(
    (...required: string[]) => {
      if (permissions === null) return true;
      return required.every((permission) => permissions.has(permission));
    },
    [permissions],
  );

  const canAny = useCallback(
    (...required: string[]) => {
      if (permissions === null) return true;
      return required.some((permission) => permissions.has(permission));
    },
    [permissions],
  );

  const settings = settingsQuery.data ?? null;

  const enumChoices = useCallback(
    (name: EnumName): EnumChoice[] => settings?.enums?.[name] ?? [],
    [settings],
  );

  const enumLabel = useCallback(
    (name: EnumName, value: string | null | undefined) => {
      if (!value) return '';
      const match = enumChoices(name).find((choice) => choice.value === value);
      return match?.label ?? value;
    },
    [enumChoices],
  );

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['session'] });
  }, [queryClient]);

  const value: SessionValue = {
    user,
    settings,
    organisations,
    organisation,
    isLoading: userQuery.isLoading || (Boolean(user) && settingsQuery.isLoading),
    isAuthenticated: Boolean(user),
    hasNoAccess: Boolean(user) && organisations.length === 0 && !user?.is_superuser,
    backendUnavailable: userQuery.isError,
    signIn,
    signOut,
    selectOrganisation,
    can,
    canAny,
    enumLabel,
    enumChoices,
    refresh,
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside SessionProvider');
  return value;
}

/** The active organisation, for screens that cannot render without one. */
export function useOrganisation() {
  const { organisation } = useSession();
  if (!organisation) throw new Error('No active organisation');
  return organisation;
}
